import { inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { concatMap, map, mergeMap, tap } from 'rxjs/operators';
import { CustomPowershellQueryResponse, CustomPowershellReport } from '../models/generated/ziroModels';
import { FileDownloadService } from '../services/file-download.service';
import { MicrosoftTenantsStateContext } from './microsoft-tenants-state.context';

@Injectable({ providedIn: 'root' })
export class CustomPowershellQueriesContext {
  private _http = inject(HttpClient);
  private _fileDownloadService = inject(FileDownloadService);
  private _tenantsStateContext = inject(MicrosoftTenantsStateContext);
  private _stateSource = new ReplaySubject<CustomPowershellReport[]>(1);
  state$ = this._stateSource.asObservable();

  baseUrl = '';

  constructor() {
    this._tenantsStateContext.cachedTenantState$.subscribe((tenantState) => {
      this._setBaseUrl(tenantState.tenantId.toString());
      this._init().subscribe();
    });
  }

  create(report: CustomPowershellReport): Observable<number> {
    return this._http.post<number>(this.baseUrl, report).pipe(
      mergeMap((reportId: number) => {
        return this._init().pipe(map(() => reportId));
      }),
      map((reportId) => reportId),
    );
  }

  update(report: CustomPowershellReport): Observable<number> {
    return this._http.put<number>(`${this.baseUrl}/${report.id}`, report).pipe(
      mergeMap(() => this._init()),
      map(() => report.id),
    );
  }

  delete(reportId: number): Observable<void> {
    return this._http.delete<void>(`${this.baseUrl}/${reportId}`).pipe(concatMap(() => this._init()));
  }

  test(query: string): Observable<CustomPowershellQueryResponse> {
    return this._http.post<CustomPowershellQueryResponse>(`${this.baseUrl}/live-queries`, { query });
  }

  export(reportId: number): Observable<void> {
    return this._fileDownloadService.downloadFile(`${this.baseUrl}/${reportId}/exports`);
  }

  private _init(): Observable<void> {
    return this._get().pipe(
      tap((data) => this._stateSource.next(data)),
      map((): null => null),
    );
  }

  private _get(): Observable<CustomPowershellReport[]> {
    return this._http.get<CustomPowershellReport[]>(this.baseUrl);
  }

  private _setBaseUrl(tenantId: string): void {
    this.baseUrl = `/services/microsoft/reporting/tenants/${tenantId}/custom-powershell-reports`;
  }
}
